import React from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { Button, DisplayText, HStack, IconLink, useResponsive } from '@garnish/core';
import { Modal } from '@garnish/overlays';

import { DefaultHeaderLeft } from '../../../../routing/components/DefaultHeaderLeft';
import { useNavigationActions } from '../../hooks';

import type { AddressType } from '@garnish/location';

export const ReorderTitle = ({
  orderType,
}: Readonly<{
  orderType: AddressType;
}>) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  return (
    <DisplayText
      sizeMatch={['24']}
      style={[styles.headline, currentBreakpoint.isXS && styles.mobileHeadline]}
    >
      {formatMessage(messages.reorderConfirmLocation, { channel: orderType })}
    </DisplayText>
  );
};

export const ReorderModalFooter = ({ isLoading, onContinue, onMoreLocations }: FooterProps) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  return (
    <Modal.Footer
      style={[styles.footer, currentBreakpoint.isXS ? undefined : styles.desktopFooter]}
      withoutTopBorder
    >
      <HStack
        style={currentBreakpoint.isXS ? styles.mobileStack : undefined}
        itemsPerRow={2}
        gap={theme.spacing['4']}
      >
        <Button
          size="large"
          palette="secondary"
          onPress={onMoreLocations}
          testID="reorder.more-locations"
          accessibilityLabel={formatMessage(messages.reorderMoreLocations)}
          accessibilityHint={formatMessage(messages.reorderMoreLocations)}
          accessibilityRole="button"
        >
          {formatMessage(messages.reorderMoreLocations)}
        </Button>
        <Button
          size="large"
          onPress={onContinue}
          isLoading={isLoading}
          testID="reorder.continue-to-bag"
          accessibilityLabel={formatMessage(messages.reorderContinueToBag)}
          accessibilityHint={formatMessage(messages.reorderContinueToBag)}
          accessibilityRole="button"
        >
          {formatMessage(messages.reorderContinueToBag)}
        </Button>
      </HStack>
    </Modal.Footer>
  );
};

export const useReorderLeftIcon = () => {
  const { formatMessage } = useIntl();
  const { onBack } = useNavigationActions();

  return (
    <DefaultHeaderLeft
      onPress={onBack}
      accessibilityLabel={formatMessage(messages.reorderConfirmLocationBack)}
    />
  );
};

export const useReorderRightIcon = () => {
  const { formatMessage } = useIntl();
  const { onClose } = useNavigationActions();
  const { currentBreakpoint } = useResponsive();
  const { right: safeAreaRight } = useSafeAreaInsets();

  // NOTE: The native header places the right item against a side inset, such as
  //       the iPhone Duo system rail, without its usual margin.
  const hasRightSafeArea = currentBreakpoint.isXS && safeAreaRight > 0;
  const closeIconStyles = [
    styles.closeIcon,
    hasRightSafeArea ? styles.closeIconWithRightSafeArea : undefined,
  ];

  return (
    <IconLink
      width={36}
      height={36}
      testID="location.search-bar.close-button"
      name="IconClose"
      palette="muted"
      style={closeIconStyles}
      accessibilityLabel={formatMessage(messages.reorderConfirmLocationClose)}
      onPress={onClose}
    />
  );
};

export type FooterProps = Readonly<{
  isLoading?: boolean;
  onContinue: () => void;
  onMoreLocations: () => void;
}>;

const styles = StyleSheet.create({
  mobileHeadline: {
    fontSize: 18,
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: 0,
    paddingTop: 0,
  },
  headline: {
    paddingHorizontal: theme.spacing['6'],
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['2'],
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: theme.spacing['4'],
    marginTop: 'auto',
  },
  desktopFooter: {
    paddingTop: theme.spacing['4'],
  },
  mobileStack: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.LIGHT,
    paddingTop: theme.spacing['4'],
  },
  closeIcon: {
    marginRight: Platform.select({
      web: theme.spacing['4'],
      default: 0,
    }),
  },
  closeIconWithRightSafeArea: {
    marginRight: theme.spacing['7'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly reorderConfirmLocationClose: NoMessageValues;
  readonly reorderConfirmLocation: { readonly channel: MessageValue };
  readonly reorderMoreLocations: NoMessageValues;
  readonly reorderContinueToBag: NoMessageValues;
  readonly reorderConfirmLocationBack: NoMessageValues;
}>({
  reorderConfirmLocationClose: {
    defaultMessage: 'Close',
    description: 'Reorder Confirm Location | Close',
  },
  reorderConfirmLocation: {
    defaultMessage: 'Confirm {channel} location',
    description: 'Reorder | Confirm Location',
  },
  reorderMoreLocations: {
    defaultMessage: 'More locations',
    description: 'Reorder | More Locations',
  },
  reorderContinueToBag: {
    defaultMessage: 'Continue to bag',
    description: 'Reorder | Continue To Bag',
  },
  reorderConfirmLocationBack: {
    defaultMessage: 'Back',
    description: 'Reorder Confirm Location | Back',
  },
});
